import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
import { cvApi } from '../../../../services/api';
import { ORGANIZATION_NAME } from '../../../../constants/radar';
import { candidateLabel, formatCvCode } from '../../../../utils/cvCode';

const DOCUMENT_TYPES = {
  PREVIEW: 'preview',
  DOCX: 'docx'
};

const DATE_CONSTANTS = {
  PRESENT: 'Present',
  DATE_FORMAT: 'YYYY.MM',
  YEAR_FORMAT: 'YYYY',
  UNTIL: 'Until'
};

const formatDateYear = (date, inProgress = false) => {
  if (inProgress) return DATE_CONSTANTS.PRESENT;

  try {
    return dayjs.utc(date).format(DATE_CONSTANTS.YEAR_FORMAT);
  } catch (error) {
    return '';
  }
};

// Helper function to format date
const formatDate = (date) => {
  if (!date) return '';
  
  try {
    // Format date as yyyy.mm
    return dayjs.utc(date).format(DATE_CONSTANTS.DATE_FORMAT);
  } catch (error) {
    return '';
  }
};

// Helper function to format date range
const formatDateRange = (startDate, endDate, isCurrent = false) => {
  const start = formatDate(startDate);
  const end = isCurrent ? DATE_CONSTANTS.PRESENT : formatDate(endDate);
  
  if (!start && !end) return '';
  if (start && !end) return start;
  if (!start && end) return `${DATE_CONSTANTS.UNTIL} ${end}`;
  
  return `${start} – ${end}`;
};

// Utility function to decode HTML entities
const decodeHtml = (html) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

// Function to safely extract text from HTML content
const parseHtmlContent = (html, type = DOCUMENT_TYPES.PREVIEW) => {
  if (!html) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = decodeHtml(html);

  const toUnicodeBold = (text) => {
    return text;
  };

  let output = '';

  const walk = (node, bold = false, depth = 0) => {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = bold ? toUnicodeBold(child.textContent) : child.textContent;
        output += text;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();
        const isBold = bold || tag === 'strong' || tag === 'b';

        if (tag === 'br') {
          output += '\n';
        } else if (tag === 'ul') {
          child.childNodes.forEach(li => {
            if (li.tagName && li.tagName.toLowerCase() === 'li') {
              const bullet = depth === 0 ? '• ' : depth === 1 ? '◦ ' : '▪ ';
              output += `${'    '.repeat(depth)}${bullet}`;
              walk(li, isBold, depth + 1);
            }
          });
        } else if (tag === 'ol') {
          let index = 1;
          child.childNodes.forEach(li => {
            if (li.tagName && li.tagName.toLowerCase() === 'li') {
              const prefix = (depth === 0) ? `${index}. ` : `${'   '.repeat(depth)}${index}. `;
              output += prefix;
              walk(li, isBold, depth + 1);
              index++;
            }
          });
        } else if (tag === 'p') {
          const isEmpty = child.childNodes.length === 0 || child.textContent.trim() === '';
          if (isEmpty) {
            output += '\n';
          } else {
            walk(child, isBold);
            output += '\n';
          }
        } else {
          walk(child, isBold);
        }
      }
    });
  };

  walk(tempDiv);
  // Use different line endings based on document type
  const lineEnding = type === DOCUMENT_TYPES.PREVIEW ? '\n' : '\r\n';
  
  return output
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    .replace(/\n/g, lineEnding)
    .trim();
};

// Function to prepare template data from CV data
const prepareTemplateData = (cvData, type = DOCUMENT_TYPES.PREVIEW) => {
  if (!cvData) return {};
  
  const transformedData = {
    fullName: cvData.fullName || cvData.employee?.fullName || '',
    // ADR-109: available to the template as {cvCode} (e.g. a footer reference)
    cvCode: cvData.code ? formatCvCode(cvData.code) : '',
    // Use custom classification if enabled, otherwise use structured fields
    position: cvData.useCustomClassification ? (cvData.customClassification || '') : (cvData.position?.name || ''),
    roles: cvData.useCustomClassification ? '' : (cvData.roles?.map(role => role.name).join(', ') || ''),
    grade: cvData.useCustomClassification ? '' : (cvData.grade?.name || ''), //TODO: add this to the template
    country: cvData.country || '',
    city: cvData.city || '',
    professionalSummary: parseHtmlContent(cvData.professionalSummary, type) || '',
    // Group skills by radar segments and order them correctly
    skills: (() => {
      const skills = cvData.skills || [];
      const radarSegments = cvData.metadata?.radarFirstLevelSegments || [];
      
      // If no radar segments available, fall back to simple grouping
      if (radarSegments.length === 0) {
        const groupedSkills = skills.reduce((grouped, skill) => {
          const segmentId = skill.firstLevelSegmentId || 'other';
          if (!grouped[segmentId]) {
            grouped[segmentId] = {
              type: skill.categoryName || 'Other',
              skills: []
            };
          }
          grouped[segmentId].skills.push({name: skill.name});
          return grouped;
        }, {});
        
        const result = Object.values(groupedSkills)
          .filter(skillCategory => skillCategory.skills && skillCategory.skills.length > 0)
          .map(skillCategory => ({
            category: skillCategory.type,
            items: skillCategory.skills.map(skill => skill.name).join(', ')
          }));
          
        return result;
      }
      
      // Use radar segments as the primary source, ordered by their order property
      const sortedSegments = radarSegments.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      const result = sortedSegments
        .map(segment => {
          // Find all skills that belong to this segment
          const segmentSkills = skills.filter(skill => skill.firstLevelSegmentId === segment.id);
          
          if (segmentSkills.length > 0) {
            return {
              category: segment.name,
              items: segmentSkills.map(skill => skill.name).join(', ')
            };
          }
          return null;
        })
        .filter(Boolean); // Remove null entries (segments with no skills)
        
      return result;
    })(),
    employmentHistory: (cvData.employmentHistory || [])
      .sort((a, b) => a.order - b.order)
      .map(job => ({
        // Use custom job title if enabled, otherwise use structured fields
        jobTitle: job.jobTitle?.useCustomJobTitle ? (job.jobTitle?.customJobTitle || '') : (job.jobTitle?.position?.name || ''),
        employer: job.employer || '',
        // Use custom project if enabled, otherwise use regular project
        project: job.useCustomProject ? (job.customProject || '') : (job.project?.name || ''),
        city: job.city || '',
        country: job.country || '',
        startDate: job.startDate,
        endDate: job.endDate,
        currentJob: job.currentJob,
        description: parseHtmlContent(job.description, type),
        responsibilities: parseHtmlContent(job.responsibilities, type),
        dateRange: formatDateRange(job.startDate, job.endDate, job.currentJob),
        grade: job.jobTitle?.useCustomJobTitle ? '' : (job.jobTitle?.grade?.name || ''), //TODO: add this to the template
        roles: job.jobTitle?.useCustomJobTitle ? '' : (job.jobTitle?.roles?.map(role => role.name).join(', ') || ''), //TODO: add this to the template
        skills: job.skills?.map(skill => skill.name).join(', ') || '' //TODO: add this to the template
      })),
    education: (cvData.education || [])
      .sort((a, b) => a.order - b.order)
      .map(edu => ({
        institution: edu.institution,
        degree: edu.educationDegree?.name || '',
        city: edu.city,
        country: edu.country,
        startDate: formatDateYear(edu.startDate),
        endDate: formatDateYear(edu.endDate, edu.inProgress),
        description: parseHtmlContent(edu.description, type)
      })),
    courses: (cvData.courses || [])
      .sort((a, b) => a.order - b.order)
      .map(course => ({
        name: course.course || '',
        institution: course.institution || '',
        dateRange: formatDateRange(course.startDate, course.endDate, course.inProgress)
      })),
    certifications: (cvData.certifications || [])
      .sort((a, b) => a.order - b.order)
      .map(cert => ({
        name: cert.name,
        issuer: cert.issuer?.name || '',
        issuedDate: formatDate(cert.issuedDate),
        website: cert.website
      })),
    languages: (cvData.languages || [])
      .sort((a, b) => a.order - b.order)
      .map(lang => ({
        name: lang.language?.name || '',
        level: lang.level ? `${lang.level.level} (${lang.level.description})` : ''
      }))
  };
  return transformedData;
};

/**
 * Creates and processes a Docxtemplater instance with the provided template data
 * @param {Object} templateData - The data to be used in the template
 * @returns {Docxtemplater} - The processed Docxtemplater instance
 * @throws {Error} - If template fetching or rendering fails
 */
const createDocxTemplate = async (templateData) => {
  try {
    // Get the template as ArrayBuffer directly from the API - it's cached after first load
    const arrayBuffer = await cvApi.getCvTemplate();

    // Load the template into PizZip
    const zip = new PizZip(arrayBuffer);
    
    // Create a parser function
    function myParser(tag) {
      return {
        get(scope) {
          const code = tag;
          const func = new Function('scope', `with(scope){ return ${code} }`);
          return func(scope);
        }
      };
    }
    
    // Create a new Docxtemplater instance with proper initialization
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      parser: myParser
    });

    // Process the template with data
    doc.render(templateData);
    return doc;
  } catch (error) {
    console.error('Template render error');
    throw new Error(`Template error: ${error.message}`);
  }
};

/**
 * Generates a downloadable DOCX file blob from CV data
 * @param {Object} cvData - The CV data to generate document from
 * @param {Object} obfuscationOptions - Options for data obfuscation
 * @returns {Blob} - The generated document as a blob
 * @throws {Error} - If document generation fails
 */
const generateDocxFile = async (cvData, obfuscationOptions = { fullName: false, employmentHistory: false, employerObfuscationType: 'previous' }) => {
  try {
    // Create a deep copy of CV data to avoid modifying the original
    const dataCopy = JSON.parse(JSON.stringify(cvData));
    
    // Apply obfuscation if needed
    if (obfuscationOptions.fullName) {
      // ADR-109: the "Candidate No." is the CV's stable, server-generated code —
      // never a random number. Fail fast rather than print a document without it.
      if (!dataCopy.code) {
        throw new Error('This CV has no code yet. Please reload the CV and try again.');
      }
      const label = candidateLabel(dataCopy.code);
      // `fullName` is what prepareTemplateData reads first; `employee.fullName` is the fallback.
      dataCopy.fullName = label;
      if (dataCopy.employee) {
        dataCopy.employee.fullName = label;
      }
    }
    
    if (obfuscationOptions.employmentHistory && dataCopy.employmentHistory) {
      // Create a map to consistently replace employer names
      const employerMap = new Map();
      let companyCounter = 1;
      
      dataCopy.employmentHistory.forEach(job => {
        if (job.employer) {
          let shouldObfuscate = false;
          
          if (obfuscationOptions.employerObfuscationType === 'all') {
            // Obfuscate all employers
            shouldObfuscate = true;
          } else if (obfuscationOptions.employerObfuscationType === 'previous') {
            // Only obfuscate employers that are not the current organization
            shouldObfuscate = job.employer !== ORGANIZATION_NAME;
          }
          
          if (shouldObfuscate) {
            // If we haven't seen this employer before, assign a new company number
            if (!employerMap.has(job.employer)) {
              employerMap.set(job.employer, `Company ${companyCounter}`);
              companyCounter++;
            }
            
            // Replace with the mapped company name
            job.employer = employerMap.get(job.employer);
          }
        }
      });
    }
    
    // Generate template data from CV data
    const templateData = prepareTemplateData(dataCopy, DOCUMENT_TYPES.DOCX);
    // Create and process template
    const doc = await createDocxTemplate(templateData);
    
    // Generate and return the blob
    return doc.getZip().generate({ type: "blob" });
  } catch (error) {
    console.error('Error generating document');
    throw error;
  }
};

export {
  formatDate,
  formatDateYear,
  formatDateRange,
  parseHtmlContent,
  DOCUMENT_TYPES,
  prepareTemplateData,
  createDocxTemplate,
  generateDocxFile,
  decodeHtml,
}; 